import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { MockDataService } from '../services/mock-data.service';

/**
 * Mock HTTP Interceptor
 * Intercepts HTTP requests when mockMode is enabled and returns mock data
 * This allows the app to run without a backend API for development/testing
 */
export const mockInterceptor: HttpInterceptorFn = (req, next) => {
  // Only intercept if mockMode is enabled
  if (!environment.mockMode) {
    return next(req);
  }

  const mockDataService = inject(MockDataService);
  const url = req.url;

  console.log(`[Mock Interceptor] Intercepting: ${req.method} ${url}`);

  // Simulate network delay (200ms)
  const delayTime = 200;

  // User Info endpoint
  if (url.includes('/user-info')) {
    return of(new HttpResponse({
      status: 200,
      body: mockDataService.getUserInfo()
    })).pipe(delay(delayTime));
  }

  // User Avatar endpoint
  if (url.includes('/user-avatar')) {
    return of(new HttpResponse({
      status: 200,
      body: mockDataService.getUserAvatar()
    })).pipe(delay(delayTime));
  }

  // Microsoft Graph - User Profile
  if (url.includes('graph.microsoft.com/v1.0/me') && !url.includes('transitiveMemberOf') && !url.includes('photo')) {
    return of(new HttpResponse({
      status: 200,
      body: mockDataService.getGraphUserProfile()
    })).pipe(delay(delayTime));
  }

  // Microsoft Graph - User Groups (for permissions)
  if (url.includes('graph.microsoft.com/v1.0/me/transitiveMemberOf')) {
    return of(new HttpResponse({
      status: 200,
      body: mockDataService.getGraphUserGroups()
    })).pipe(delay(delayTime));
  }

  // Microsoft Graph - User Photo
  if (url.includes('graph.microsoft.com/v1.0/me/photo')) {
    // Return blob-like data
    const base64Avatar = mockDataService.getUserAvatar().data.split(',')[1];
    const blob = new Blob([atob(base64Avatar)], { type: 'image/svg+xml' });
    return of(new HttpResponse({
      status: 200,
      body: blob
    })).pipe(delay(delayTime));
  }

  // Downtime endpoint - GET
  if (url.includes('/downtime') && req.method === 'GET') {
    return of(new HttpResponse({
      status: 200,
      body: mockDataService.getDowntime()
    })).pipe(delay(delayTime));
  }

  // Downtime endpoint - POST (admin setting downtime)
  if (url.includes('/downtime') && req.method === 'POST') {
    return of(new HttpResponse({
      status: 200,
      body: {
        data: {
          success: true,
          message: 'Downtime status updated (mock)'
        }
      }
    })).pipe(delay(delayTime));
  }

  // Online Help endpoint - GET
  if (url.includes('/online-help') && req.method === 'GET') {
    return of(new HttpResponse({
      status: 200,
      body: mockDataService.getOnlineHelp()
    })).pipe(delay(delayTime));
  }

  // Online Help endpoint - POST (upload help files)
  if (url.includes('/online-help') && req.method === 'POST') {
    return of(new HttpResponse({
      status: 200,
      body: {
        data: {
          success: true,
          message: 'Help content uploaded successfully (mock)'
        }
      }
    })).pipe(delay(delayTime));
  }

  // Screening endpoint
  if (url.includes('/screening') && req.method === 'POST') {
    const requestBody = req.body;
    return of(new HttpResponse({
      status: 200,
      body: mockDataService.getScreeningResult(requestBody)
    })).pipe(delay(500)); // Longer delay for screening to simulate processing
  }

  // LCD Files endpoint - GET
  if (url.includes('/lcd-files') && req.method === 'GET') {
    return of(new HttpResponse({
      status: 200,
      body: mockDataService.getLcdFiles()
    })).pipe(delay(delayTime));
  }

  // LCD Files endpoint - POST (upload)
  if (url.includes('/lcd-files') && req.method === 'POST') {
    return of(new HttpResponse({
      status: 200,
      body: {
        data: {
          success: true,
          message: 'LCD file uploaded successfully (mock)',
          fileId: `lcd-${Date.now()}`
        }
      }
    })).pipe(delay(delayTime));
  }

  // NCD Files endpoint - GET
  if (url.includes('/ncd-files') && req.method === 'GET') {
    return of(new HttpResponse({
      status: 200,
      body: mockDataService.getNcdFiles()
    })).pipe(delay(delayTime));
  }

  // NCD Files endpoint - POST (upload)
  if (url.includes('/ncd-files') && req.method === 'POST') {
    return of(new HttpResponse({
      status: 200,
      body: {
        data: {
          success: true,
          message: 'NCD file uploaded successfully (mock)',
          fileId: `ncd-${Date.now()}`
        }
      }
    })).pipe(delay(delayTime));
  }

  // Facilities endpoint - GET
  if (url.includes('/facilities') && req.method === 'GET') {
    return of(new HttpResponse({
      status: 200,
      body: mockDataService.getFacilities()
    })).pipe(delay(delayTime));
  }

  // Facilities endpoint - POST (create/update)
  if (url.includes('/facilities') && (req.method === 'POST' || req.method === 'PUT')) {
    return of(new HttpResponse({
      status: 200,
      body: {
        data: {
          success: true,
          message: 'Facility saved successfully (mock)'
        }
      }
    })).pipe(delay(delayTime));
  }

  // Users endpoint - GET
  if (url.includes('/users') && req.method === 'GET') {
    return of(new HttpResponse({
      status: 200,
      body: mockDataService.getUsers()
    })).pipe(delay(delayTime));
  }

  // Users endpoint - POST (create/update)
  if (url.includes('/users') && (req.method === 'POST' || req.method === 'PUT')) {
    return of(new HttpResponse({
      status: 200,
      body: {
        data: {
          success: true,
          message: 'User saved successfully (mock)'
        }
      }
    })).pipe(delay(delayTime));
  }

  // Reports endpoint
  if (url.includes('/reports')) {
    return of(new HttpResponse({
      status: 200,
      body: mockDataService.getReportsData()
    })).pipe(delay(delayTime));
  }

  // ABN endpoint
  if (url.includes('/abn') && req.method === 'POST') {
    const requestBody = req.body;
    return of(new HttpResponse({
      status: 200,
      body: mockDataService.generateAbn(requestBody)
    })).pipe(delay(delayTime));
  }

  // If no mock handler matched, log and pass through to real API
  console.warn(`[Mock Interceptor] No mock handler for: ${req.method} ${url}`);
  console.warn('[Mock Interceptor] Passing through to real API (may fail if backend is not available)');

  return next(req);
};
